import { createServerFn } from '@tanstack/react-start';
import z from 'zod';

import { apiClient } from '@/utils/api-client';
import { createFormData, HTTPError } from '@/utils/http-client';

type ConsentRequest = {
  client_name: string;
  redirect_host: string;
};

export type ConsentRequestLookup =
  | { status: 'ok'; request: ConsentRequest }
  | { status: 'expired' }
  | { status: 'unavailable' };

type ApproveResponse = {
  redirect_to: string;
};

export const getConsentRequestServerFn = createServerFn({ method: 'GET' })
  .inputValidator(z.object({ request_id: z.string().min(1) }))
  .handler(async ({ data: { request_id } }): Promise<ConsentRequestLookup> => {
    const client = await apiClient({ authenticated: false });
    try {
      const request = await client.get<ConsentRequest>(
        `/oauth/authorize/request?request_id=${encodeURIComponent(request_id)}`,
      );
      return { status: 'ok', request };
    } catch (error) {
      // Only the backend's own rejection means the request is gone; anything else (5xx,
      // network) is a page the user can simply reload, and telling them to restart the
      // agent instead would be wrong.
      if (error instanceof HTTPError && [400, 404].includes(error.response.status)) return { status: 'expired' };
      return { status: 'unavailable' };
    }
  });

export const approveConsentServerFn = createServerFn({ method: 'POST' })
  .inputValidator(z.object({ request_id: z.string().min(1) }))
  .handler(async ({ data: { request_id } }) => {
    // Authenticated on purpose: the backend takes the approving user from the verified token,
    // never from anything this page asserts.
    const client = await apiClient({ authenticated: true });
    return client.post<ApproveResponse>('/oauth/authorize/approve', createFormData({ request_id }));
  });

export const declineConsentServerFn = createServerFn({ method: 'POST' })
  .inputValidator(z.object({ request_id: z.string().min(1) }))
  .handler(async ({ data: { request_id } }) => {
    // Declining redirects back to the application with `access_denied`, so it learns the
    // outcome instead of waiting out the request's expiry.
    const client = await apiClient({ authenticated: true });
    return client.post<ApproveResponse>('/oauth/authorize/decline', createFormData({ request_id }));
  });
